'use client';
import React from 'react';
import { QRType } from './QRGeneratorClient';

const qrTypes: { key: QRType; label: string; icon: React.ReactNode; badge?: string }[] = [
  {
    key: 'url',
    label: 'Website',
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20M12 2c-2.8 3-4.5 6.3-4.5 10s1.7 7 4.5 10M12 2c2.8 3 4.5 6.3 4.5 10s-1.7 7-4.5 10" />
      </svg>
    ),
  },
  {
    key: 'text',
    label: 'Text',
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M4 7V4h16v3M9 20h6M12 4v16" />
      </svg>
    ),
  },
  {
    key: 'whatsapp',
    label: 'WhatsApp',
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
        <path d="M12 2A10 10 0 0 1 22 12 10 10 0 0 1 12 22c-1.85 0-3.58-.5-5.07-1.38L2 22l1.4-4.8A9.96 9.96 0 0 1 2 12 10 10 0 0 1 12 2Z" stroke="currentColor" strokeWidth="1.6" />
        <path d="M9 9.5c0-.42.34-.75.75-.75h.75a.75.75 0 0 1 .75.75l.45 1.8a.75.75 0 0 1-.21.75l-.54.54a6.75 6.75 0 0 0 2.46 2.46l.54-.54a.75.75 0 0 1 .75-.21l1.8.45a.75.75 0 0 1 .75.75v.75a.75.75 0 0 1-.75.75C12.03 17.25 6.75 11.97 6.75 9.5" fill="currentColor" />
      </svg>
    ),
    badge: 'Popular',
  },
  {
    key: 'phone',
    label: 'Phone',
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" />
      </svg>
    ),
  },
  {
    key: 'email',
    label: 'Email',
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="M2 7l10 7 10-7" />
      </svg>
    ),
  },
  {
    key: 'maps',
    label: 'Maps',
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M12 2a7 7 0 0 1 7 7c0 5.25-7 13-7 13S5 14.25 5 9a7 7 0 0 1 7-7Z" />
        <circle cx="12" cy="9" r="2.5" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    key: 'wifi',
    label: 'WiFi',
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M1.5 8.5A15 15 0 0 1 22.5 8.5M5 12.5a10 10 0 0 1 14 0M8.5 16.5a5 5 0 0 1 7 0" />
        <circle cx="12" cy="20" r="1.2" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    key: 'upi',
    label: 'UPI',
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <rect x="2" y="5" width="20" height="14" rx="3" />
        <path d="M2 10h20M7 15h3M15 15h2" />
      </svg>
    ),
    badge: 'India',
  },
];

interface QRTypeSelectorProps {
  activeType: QRType;
  onTypeChange: (type: QRType) => void;
}

export default function QRTypeSelector({ activeType, onTypeChange }: QRTypeSelectorProps) {
  return (
    <div className="bg-card rounded-3xl border border-border shadow-card p-5">
      <h2 className="font-semibold text-foreground mb-4" style={{ fontSize: '15px' }}>QR Code Type</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {qrTypes.map((type) => {
          const isActive = activeType === type.key;
          return (
            <button
              key={`qrtype-${type.key}`}
              onClick={() => onTypeChange(type.key)}
              className={`relative flex flex-col items-center justify-center gap-2.5 rounded-2xl transition-all duration-200 border ${
                isActive
                  ? 'border-transparent text-white shadow-blue'
                  : 'border-border text-muted-foreground hover:border-primary/30 hover:bg-secondary hover:text-foreground'
              }`}
              style={{
                minHeight: '88px',
                padding: '16px 12px',
                background: isActive ? 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)' : undefined,
              }}
            >
              {type.badge && (
                <span
                  className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full text-white"
                  style={{
                    fontSize: '9px',
                    fontWeight: 700,
                    background: isActive ? 'rgba(255,255,255,0.25)' : 'var(--primary)',
                    letterSpacing: '0.02em',
                    zIndex: 1,
                  }}
                >
                  {type.badge}
                </span>
              )}
              <span className={`flex-shrink-0 ${isActive ? 'text-white' : ''}`}>{type.icon}</span>
              <span style={{ fontSize: '13px', fontWeight: 600, lineHeight: 1.2 }}>{type.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}